import {useState, useEffect} from "react";
import {confirmPasswordReset, verifyPasswordResetCode} from "firebase/auth";
import {auth} from "../../firebaseApp.ts";
import FormError from "../login/FormError.tsx";
import LoadingFrame from "../../components/LoadingFrame.tsx";
import {FormTodo, FormTodoType} from "../../components/form/FormTodo.tsx";
import {validateForm, Validator} from "../../components/form/validation.ts";
import validator from "validator";
import {PasswordInput} from "../../components/form/PasswordInput.tsx";
import {Logo} from "../../components/specific/Logo.tsx";

interface Props {
    actionCode: string;
    continueUrl: string;
}

interface ResetPasswordFormData {
    new_password: string;
    repeat_new_password: string;
}

const validators: Array<Validator<ResetPasswordFormData>> = [
    {
        fieldName: 'new_password',
        validationFunction: (data: ResetPasswordFormData) => {
            return validator.isLength(data.new_password, {min: 8, max: 255})
        },
        message: 'Wachtwoord moet tussen 8 en 255 tekens zijn'
    },
    {
        fieldName: 'new_password',
        validationFunction: (data: ResetPasswordFormData) => {
            return /[a-z]/.test(data.new_password);
        },
        message: 'Wachtwoord moet minstens één kleine letter bevatten'
    },
    {
        fieldName: 'new_password',
        validationFunction: (data: ResetPasswordFormData) => {
            return /[A-Z]/.test(data.new_password);
        },
        message: 'Wachtwoord moet minstens één hoofdletter bevatten'
    },
    {
        fieldName: 'new_password',
        validationFunction: (data: ResetPasswordFormData) => {
            return /[0-9]/.test(data.new_password);
        },
        message: 'Wachtwoord moet minstens één cijfer bevatten'
    },
    {
        fieldName: 'new_password',
        validationFunction: (data: ResetPasswordFormData) => {
            const specificSpecialCharsRegex = /[\!"#\$%\&'\(\)\*\+,\-\.\/\:\;\<\=\>\?\@\[\\\]\^\_\`\{\|\}\~]/;
            return specificSpecialCharsRegex.test(data.new_password);
        },
        message: 'Wachtwoord moet minstens één speciaal teken bevatten'
    },
    {
        fieldName: 'repeat_new_password',
        validationFunction: (data: ResetPasswordFormData) => {
            return validator.equals(data.repeat_new_password, data.new_password)
        },
        message: 'Wachtwoorden komen niet overeen'
    }
]

export function ResetPasswordActionHandler({actionCode, continueUrl}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [formError, setFormError] = useState('');
    const [success, setSuccess] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [formData, setFormData] = useState<ResetPasswordFormData>({
        new_password: '',
        repeat_new_password: ''
    });
    const [formTodos, setFormTodos] = useState<Array<FormTodoType>>([]);

    useEffect(() => {
        async function verifyCode() {
            if (!actionCode) {
                setFormError('Ongeldige resetlink');
                return;
            }

            setIsVerifying(true);
            try {
                const email = await verifyPasswordResetCode(auth, actionCode);
                setUserEmail(email);
                setIsVerifying(false);
            } catch (e: any) {
                setFormError(e);
                setIsVerifying(false);
            }
        }

        verifyCode();
    }, [actionCode]);

    function validate(): boolean {
        const validationResult = validateForm(formData, validators);
        setFormTodos(validationResult.todos);
        return validationResult.success;
    }

    async function handlePasswordReset(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        
        if (!actionCode) {
            setFormError('Ongeldige resetlink');
            return;
        }

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setFormError('');

        try {
            await confirmPasswordReset(auth, actionCode, formData.new_password);
            setSuccess(true);
            
            setTimeout(() => {
                if (continueUrl) {
                        window.location.href = continueUrl;
                } 
            }, 2000);
        } catch (e: any) {
            setFormError(e);
        } finally {
            setIsLoading(false);
        }
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 px-4">
                <div className="max-w-md w-full text-center space-y-8">
                    <div className="flex justify-center">
                        <Logo/>
                    </div>
                    <div className="flex items-center justify-center">
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center border-2 border-green-200">
                            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <h1 className="text-3xl font-bold text-gray-900">Wachtwoord opnieuw ingesteld</h1>
                        <p className="text-lg text-gray-600">Je wachtwoord is succesvol opnieuw ingesteld. Je wordt doorgestuurd naar de inlogpagina...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 px-4">
            <div className="max-w-md w-full">
                <LoadingFrame isLoading={isLoading || isVerifying}>
                    <div className="space-y-6">
                        <div className="flex justify-center">
                            <Logo/>
                        </div>
                        <div className="text-center space-y-4">
                            <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                                <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900">Stel je wachtwoord opnieuw in</h1>
                            {userEmail && (
                                <p className="text-gray-600">
                                    Wachtwoord opnieuw instellen voor: <strong>{userEmail}</strong>
                                </p>
                            )}
                        </div>
                        
                        {formError && <FormError message={formError}/>}
                        
                        <form className="space-y-6" onSubmit={handlePasswordReset}>
                            <div>
                                <label htmlFor="new_password" className="block text-sm font-medium leading-6 text-gray-900">
                                    Nieuw wachtwoord
                                </label>
                                <div className="mt-2">
                                    <PasswordInput
                                        id="new_password"
                                        name="new_password"
                                        required
                                        value={formData.new_password}
                                        onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                                        autoComplete="new-password"
                                        formTodos={formTodos}
                                        fieldName="new_password"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="repeat_new_password" className="block text-sm font-medium leading-6 text-gray-900">
                                    Herhaal nieuw wachtwoord
                                </label>
                                <div className="mt-2">
                                    <PasswordInput
                                        id="repeat_new_password"
                                        name="repeat_new_password"
                                        required
                                        value={formData.repeat_new_password}
                                        onChange={(e) => setFormData({...formData, repeat_new_password: e.target.value})}
                                        autoComplete="new-password"
                                        formTodos={formTodos}
                                        fieldName="repeat_new_password"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white shadow-lg hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                
                                    <span>Wachtwoord opnieuw instellen</span>
                            
                            </button>
                        </form>
                    </div>
                </LoadingFrame>
            </div>
        </div>
    );
}

